import CameraAltRounded from '@mui/icons-material/CameraAltRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LinkedCamera from '@mui/icons-material/LinkedCamera';
import UploadRounded from '@mui/icons-material/UploadRounded';
import { LoadingButton } from '@mui/lab';
import { Box, MenuItem, Popover, Tooltip, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';

import { useSettings } from '@hooks/useSettings';

import { Steps } from '../types';

type ButtonType = 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';

type Props = {
  type: ButtonType;
  label: string;
  isMobile?: boolean;
  disabled?: boolean;
  onClick: (param: Steps) => void;
};

type Settings = {
  AutomatedCaptureEnabled: boolean;
};

type MenuItemType = {
  title: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
};

const settings = ['AutomatedCaptureEnabled'];

const Buttons = ({ type, label, isMobile = false, disabled = false, onClick }: Props) => {
  const [open, setOpen] = useState<HTMLButtonElement | null>(null);
  const { AutomatedCaptureEnabled } = useSettings(settings, 'mailroomSettingValue') as Settings;

  const menu: MenuItemType[] = [
    {
      title: 'Single',
      icon: <CameraAltRounded />,
      onClick: () => onClick(Steps.Photo),
    },
    {
      title: 'Automated',
      icon: <LinkedCamera />,
      onClick: () => onClick(Steps.AutomatedCapture),
      disabled: !AutomatedCaptureEnabled,
      tooltip: 'Contact sales to enable Automated capture',
    },
    {
      title: 'Upload Images',
      icon: <UploadRounded />,
      onClick: () => onClick(Steps.Images),
    },
  ];

  return (
    <Box sx={{ position: 'relative', width: isMobile && !disabled ? '49%' : 'auto', height: '100%' }}>
      <LoadingButton
        variant={type === 'primary' ? 'contained' : 'outlined'}
        onClick={(event) => setOpen(event.currentTarget)}
        key={label}
        color={type}
        size={isMobile ? 'large' : 'medium'}
        sx={{ width: isMobile ? '100%' : 'auto', height: '100%' }}
        disabled={disabled}
        endIcon={!isMobile && (open ? <ExpandLess /> : <ExpandMore />)}
      >
        {label}
      </LoadingButton>
      <Popover
        disablePortal
        open={Boolean(open)}
        anchorEl={open}
        onClose={() => setOpen(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {menu.map((item) => {
          const menuItem = (
            <MenuItem
              onClick={() => {
                item.onClick();
                setOpen(null);
              }}
              disabled={item.disabled}
            >
              {item.icon}
              <Typography variant="body2" sx={{ ml: 2 }}>
                {item.title}
              </Typography>
            </MenuItem>
          );

          return (
            <Box component="span" key={item.title}>
              {item.disabled && item.tooltip ? (
                <Tooltip arrow title={item.tooltip} placement="left">
                  <Box component="span">{menuItem}</Box>
                </Tooltip>
              ) : (
                menuItem
              )}
            </Box>
          );
        })}
      </Popover>
    </Box>
  );
};

const ContinueCapture = (props: Props) => {
  return props.disabled ? (
    <Tooltip
      sx={{ width: props.isMobile ? '49%' : 'auto' }}
      title={
        <Box sx={{ textAlign: 'center' }}>
          This session is closed. <br /> All items are already sent.
        </Box>
      }
    >
      <Box>
        <Buttons {...props} />
      </Box>
    </Tooltip>
  ) : (
    <Buttons {...props} />
  );
};

export default ContinueCapture;
